import { CircleHelp } from "lucide-react";
import { useMemo } from "react";
import { Link, useLocation } from "react-router";
import { ACCOUNTING_FUSION_HELP_PAGE_PATH } from "@/shared/configs/routes.config";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import {
  formatAmount,
  getSourceLabel,
  normalizeComparable,
  normalizeRef,
} from "./utils";
import type { FusionReason } from "../../core/models/admin";
import type { BankMovement } from "../../types/accounting.types";

// Ce que l'API a reconnu entre le mouvement ouvert et la ligne proposée.
const FUSION_REASON_LABELS: Record<FusionReason, string> = {
  payment_number_in_description: "Numéro de paiement dans le libellé banque",
  payment_numbers_overlap: "Numéros de paiement concordants",
  bridge_transfer_reference: "Référence du virement Bridge dans le libellé banque",
  bridge_transfer_date: "Même montant à la date d'exécution du virement Bridge",
};

type FusionPanelProps = {
  bankMovement: BankMovement
  fusionSearch: string
  setFusionSearch: (value: string) => void
  fusionCandidates: BankMovement[]
  isFusionCandidatesLoading: boolean
  onFuse: (candidateId: string) => void
  onSplit: () => void
  isFusionBusy: boolean
  isSplitBusy: boolean
  fusingCandidateId: string | null
  isBridgeMovement: boolean
};

const FusionPanel = ({
  bankMovement,
  fusionSearch,
  setFusionSearch,
  fusionCandidates,
  isFusionCandidatesLoading,
  onFuse,
  onSplit,
  isFusionBusy,
  isSplitBusy,
  fusingCandidateId,
  isBridgeMovement,
}: FusionPanelProps) => {
  const location = useLocation();
  // YTI-1732 : le score vient de l'API (`fusion_score`, `fusion_reasons`), la
  // même règle que le balayage qui pose le badge dans le tableau. Tous les
  // candidats au meilleur score sont surlignés : deux lignes banque à la
  // même date et au même montant sont deux propositions, pas une. Le calcul
  // local sur le numéro de paiement reste le repli des réponses sans score.
  const probableCandidateIds = useMemo(() => {
    if (fusionCandidates.length === 0) return new Set<string>();

    const hasServerScores = fusionCandidates.some((candidate) => candidate.fusionScore !== null && candidate.fusionScore !== undefined);
    if (hasServerScores) {
      // Seuls les candidats que l'API juge probables sont surlignés : la seule
      // proximité de date reste affichée comme raison, sans le badge.
      const probable = fusionCandidates.filter((candidate) => candidate.fusionProbable === true);
      if (probable.length === 0) return new Set<string>();
      const best = Math.max(...probable.map((candidate) => candidate.fusionScore ?? 0));
      return new Set(probable.filter((candidate) => (candidate.fusionScore ?? 0) === best).map((candidate) => candidate.id));
    }

    const currentPaymentRef = normalizeComparable(bankMovement.paymentNumber);
    const currentBankDescription = normalizeComparable(bankMovement.bridgeBankDescription);
    let bestCandidateId: string | null = null;
    let bestScore = 0;

    fusionCandidates.forEach((candidate) => {
      const candidateBankDescription = normalizeComparable(candidate.bridgeBankDescription);
      const candidatePaymentRef = normalizeComparable(candidate.paymentNumber);
      let score = 0;
      if (currentPaymentRef && candidateBankDescription.includes(currentPaymentRef)) score += 6;
      if (currentPaymentRef && candidatePaymentRef
        && (candidatePaymentRef.includes(currentPaymentRef) || currentPaymentRef.includes(candidatePaymentRef))) {
        score += 4;
      }
      if (candidatePaymentRef && currentBankDescription.includes(candidatePaymentRef)) score += 5;
      if (score > bestScore) {
        bestScore = score;
        bestCandidateId = candidate.id;
      }
    });

    return bestCandidateId ? new Set<string>([bestCandidateId]) : new Set<string>();
  }, [bankMovement, fusionCandidates]);

  const orderedFusionCandidates = useMemo(() => {
    if (probableCandidateIds.size === 0) return fusionCandidates;
    return [...fusionCandidates].sort((left, right) => {
      const leftProbable = probableCandidateIds.has(left.id) ? 0 : 1;
      const rightProbable = probableCandidateIds.has(right.id) ? 0 : 1;
      return leftProbable - rightProbable;
    });
  }, [fusionCandidates, probableCandidateIds]);

  if (bankMovement.dateFusion) {
    return (
      <div className="select-text">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs font-medium text-gray-800">Écriture fusionnée</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Fusionnée le
              {" "}
              {bankMovement.dateFusion}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-7 text-[11px]"
            onClick={() => onSplit()}
            disabled={isSplitBusy}
            aria-busy={isSplitBusy}
          >
            {isSplitBusy ? "Dissociation..." : "Dissocier (Split)"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="select-text space-y-2">
      {isBridgeMovement && (
        <div className="grid grid-cols-2 gap-2 p-2 rounded-md border bg-slate-50/70">
          <div>
            <div className="text-[10px] text-gray-500 mb-0.5">Description</div>
            <div className="text-[11px] text-gray-800 leading-tight break-words">{bankMovement.bridgeBankDescription || "-"}</div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 mb-0.5">Label</div>
            <div className="text-[11px] text-gray-800 leading-tight break-words">{bankMovement.bridgeCleanDescription || "-"}</div>
          </div>
        </div>
      )}
      {!isBridgeMovement && bankMovement.brPaymentTransactionId && bankMovement.rawId && (
        <div className="p-2 rounded-md border bg-slate-50/70">
          <div className="text-[10px] text-gray-500 mb-0.5">Référence Bridge</div>
          <div className="text-[11px] text-gray-800 leading-tight">
            <span className="font-mono">{`M${bankMovement.rawId}`}</span>
            {" "}
            <span className="text-gray-500">à retrouver dans le libellé de la ligne banque</span>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-[11px] font-medium text-gray-800 select-text">Fusion d'écriture</div>
        {fusionCandidates.length > 0 && (
          <Link
            to={ACCOUNTING_FUSION_HELP_PAGE_PATH}
            state={{ from: `${location.pathname}${location.search}` }}
            className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
          >
            <CircleHelp className="h-3.5 w-3.5" />
            Pourquoi fusionner ?
          </Link>
        )}
      </div>
      <div className="space-y-2">
        <Input
          value={fusionSearch}
          onChange={(event) => setFusionSearch(event.target.value)}
          placeholder="Rechercher..."
          className="h-7 text-[11px]"
        />
        <div className="max-h-52 overflow-auto border rounded-md">
          {isFusionCandidatesLoading && (
            <div className="p-2 text-xs text-gray-500">Chargement des propositions...</div>
          )}
          {!isFusionCandidatesLoading && orderedFusionCandidates.length === 0 && (
            <div className="p-2 text-xs text-gray-500">Aucune proposition de fusion.</div>
          )}
          {!isFusionCandidatesLoading && orderedFusionCandidates.map((candidate) => {
            const isBridgeCandidate = candidate.source?.toLowerCase().includes("bridge");
            const isProbable = probableCandidateIds.has(candidate.id);
            const reasons = (candidate.fusionReasons ?? []).map((reason) => FUSION_REASON_LABELS[reason]).filter(Boolean);
            const netAmount = (candidate.credit ?? 0) - (candidate.debit ?? 0);
            const paymentRef = !isBridgeCandidate ? normalizeRef(candidate.paymentNumber) : null;

            return (
              <div
                key={candidate.id}
                className={[
                  "p-2.5 border-b last:border-b-0 select-text",
                  isProbable ? "bg-violet-50/70 border-violet-200" : "",
                ].join(" ")}
              >
                {/* Header: label + badge + amount */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs font-medium text-gray-800 truncate select-text">
                      {candidate.label || "-"}
                    </span>
                    {isProbable && (
                      <span className="shrink-0 inline-flex items-center rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                        Probable
                      </span>
                    )}
                  </div>
                  <span className={[
                    "shrink-0 text-xs font-semibold tabular-nums",
                    netAmount < 0 ? "text-red-600" : "text-emerald-600",
                  ].join(" ")}
                  >
                    {netAmount < 0 ? "-" : "+"}
                    {formatAmount(Math.abs(netAmount))}
                    {" "}
                    €
                  </span>
                </div>

                {/* Meta row: date, ref, source */}
                <div className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-500 select-text">
                  {/* Une saisie payée par Bridge peut n'avoir pas encore de date : la
                      date d'exécution annoncée par Bridge dit quand la banque a payé. */}
                  {candidate.date
                    ? <span>{candidate.date}</span>
                    : candidate.brDate
                      ? <span>{`Exécuté par Bridge le ${candidate.brDate}`}</span>
                      : candidate.issueDate
                        ? <span>{`Émis le ${candidate.issueDate}`}</span>
                        : <span>-</span>}
                  {!isBridgeCandidate && candidate.brPaymentTransactionId && candidate.rawId && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span>
                        Réf. Bridge
                        {" "}
                        <span className="font-mono">{`M${candidate.rawId}`}</span>
                      </span>
                    </>
                  )}
                  {paymentRef && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span>
                        Réf.
                        {paymentRef}
                      </span>
                    </>
                  )}
                  <span className="text-gray-300">·</span>
                  <span className={[
                    "inline-flex items-center rounded px-1 py-px text-[10px] font-medium",
                    isBridgeCandidate
                      ? "bg-blue-50 text-blue-600"
                      : "bg-gray-100 text-gray-500",
                  ].join(" ")}
                  >
                    {getSourceLabel(candidate.source)}
                  </span>
                </div>

                {reasons.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {reasons.map((reason) => (
                      <span
                        key={reason}
                        className={[
                          "inline-flex items-center rounded px-1.5 py-px text-[10px]",
                          isProbable ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600",
                        ].join(" ")}
                      >
                        {reason}
                      </span>
                    ))}
                  </div>
                )}

                {/* Bridge descriptions — only show if different from label */}
                {isBridgeCandidate && (
                  (candidate.bridgeCleanDescription && candidate.bridgeCleanDescription !== candidate.label)
                  || candidate.bridgeBankDescription
                ) && (
                  <div className="mt-1.5 rounded bg-slate-50 px-2 py-1.5 text-[11px] text-gray-500 space-y-0.5 select-text">
                    {candidate.bridgeCleanDescription && candidate.bridgeCleanDescription !== candidate.label && (
                      <div className="truncate select-text">{candidate.bridgeCleanDescription}</div>
                    )}
                    {candidate.bridgeBankDescription && (
                      <div className="truncate font-mono text-[10px] text-gray-400 select-text">{candidate.bridgeBankDescription}</div>
                    )}
                  </div>
                )}

                {/* Action */}
                <div className="mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={isProbable ? "action" : "outline"}
                    className={isProbable ? "h-7 text-xs" : "h-7 text-xs"}
                    onClick={() => onFuse(candidate.id)}
                    disabled={isFusionBusy}
                    aria-busy={fusingCandidateId === candidate.id}
                  >
                    {fusingCandidateId === candidate.id ? "Fusion en cours..." : "Fusionner"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FusionPanel;
